import React, { useState, useEffect, useContext } from "react";
import { Layout, Menu, message, Modal } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DashboardFilled,
  CarFilled,
  PayCircleFilled,
  CreditCardFilled,
  BarChartOutlined,
  ToolFilled,
  SettingFilled,
  UserOutlined,
  AuditOutlined,
  WalletOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { FaGasPump } from "react-icons/fa";
import logo from "../../Assets/Images/logo.svg";
import logout from "../../Assets/Images/logout.svg";
import { UserContext } from "../../App";
import {
  handleLogout,
  getRefreshToken,
} from "../../Utlis/UpdateUserState";
import { logout as logoutApi } from "../../API/auth";
import { getManagerProfile } from "../../API/FleetManager/MyProfile";
import useProfileSync from "../../Utlis/useProfileSync";

import "./style.css";
import InitialsAvatar from "../InitialsAvatar";

const { Sider } = Layout;

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const SidebarManager = ({
  collapsed,
  setCollapsed,
  breakpoint,
  user,
  setUser,
}) => {
  const { userData, setUserToken } = useContext(UserContext);
  const location = useLocation();
  const [selectedId, setSelectedId] = useState("1");
  const navigate = useNavigate();
  const pageAccess = userData?.page_access || {};
  const [profileInfo, setProfileInfo] = useState({
    name: "",
    role: "",
    avatar: null,
  });

  useProfileSync(getManagerProfile, (data) => {
    setProfileInfo({
      name: data.name || "",
      role: data.role || "",
      avatar: resolveAvatarUrl(data.avatar),
    });
  });

  useEffect(() => {
    if (!userData?.avatar) return;

    setProfileInfo((prev) => ({
      ...prev,
      avatar: resolveAvatarUrl(userData.avatar),
    }));
  }, [userData?.avatar]);

  useEffect(() => {
    if (!userData?.name && !userData?.role) return;

    setProfileInfo((prev) => ({
      ...prev,
      name: userData.name || prev.name,
      role: userData.role || prev.role,
    }));
  }, [userData?.name, userData?.role]);

  const pageAccessSlugMap = {
    1: "dashboard",
    2: "vehicles",
    3: "drivers",
    4: "fuel-vendors",
    5: "credits-billing",
    6: "subscription-payments",
    7: "transactions",
    8: "reports",
    9: "user-management",
    10: "settings-permissions",
    11: "audit-trail",
    12: "company-details",
  };

  const isPageVisible = (key) =>
    pageAccess[pageAccessSlugMap[key]] !== "restrict";

  const routeKeyMap = {
    "/dashboard-manager": "1",
    "/vehicles-manager": "2",
    "/drivers": "3",
    "/fuel": "4",
    "/credits-billings-manager": "5",
    "/subscription-payments": "6",
    "/transactions-manager": "7",
    "/reports-manager": "8",
    "/user-management-manager": "9",
    "/settings-permissions-manager": "10",
    "/audit-trail-manager": "11",
    "/company-details": "12",
  };

  useEffect(() => {
    const path = location.pathname;

    const matchedRoute = Object.keys(routeKeyMap).find((route) =>
      path.startsWith(route),
    );

    if (matchedRoute) {
      setSelectedId(routeKeyMap[matchedRoute]);
    } else if (path.startsWith("/profile-manager")) {
      // Profile isn't a menu page, so nothing should appear selected
      setSelectedId("");
    }
  }, [location.pathname]);

  const items = [
    {
      type: "group",
      label: "All Pages",
      children: [
        {
          key: "1",
          icon: <DashboardFilled />,
          label: <Link to="/dashboard-manager">Dashboard</Link>,
        },
        {
          key: "2",
          icon: <CarFilled />,
          label: <Link to="/vehicles-manager">Vehicles</Link>,
        },
        {
          key: "3",
          icon: <UserOutlined />,
          label: <Link to="/drivers-manager">Drivers</Link>,
        },

        {
          key: "4",
          icon: <FaGasPump />,
          label: <Link to="/fuel-vendors-manager">Fuel Vendors</Link>,
        },
        {
          key: "5",
          icon: <CreditCardFilled />,
          label: <Link to="/credits-billings-manager">Credits & Billing</Link>,
        },
        {
          key: "6",
          icon: <WalletOutlined />,
          label: (
            <Link to="/subscription-payments">Subscription Payments </Link>
          ),
        },
        {
          key: "7",
          icon: <PayCircleFilled />,
          label: <Link to="/transactions-manager">Transactions</Link>,
        },
        {
          key: "8",
          icon: <BarChartOutlined />,
          label: <Link to="/reports-manager">Reports</Link>,
        },

        {
          key: "9",
          icon: <ToolFilled />,
          label: <Link to="/user-management-manager">User Management</Link>,
        },
        {
          key: "10",
          icon: <SettingFilled />,
          label: (
            <Link to="/settings-permissions-manager">
              Settings & Permissions
            </Link>
          ),
        },
        {
          key: "11",
          icon: <AuditOutlined />,
          label: <Link to="/audit-trail-manager">Audit Trail</Link>,
        },
        {
          key: "12",
          icon: <BankOutlined />,
          label: <Link to="/company-details">Company Detail</Link>,
        },
      ].filter((item) => isPageVisible(item.key)),
    },
  ];

  const handleProfileClick = () => {
    navigate("/profile-manager");
  };

  return (
    <Sider
      className="sidebar"
      width={240}
      collapsible
      collapsed={collapsed}
      onCollapse={(val) => setCollapsed(val)}
      collapsedWidth={80}
      breakpoint="lg"
      onBreakpoint={(broken) => {
        setCollapsed(broken);
      }}
      theme="light"
      style={{ height: "100vh", position: "fixed" }}
    >
      <div className={`logo-container ${collapsed ? "collapsed" : ""}`}>
        <img src={logo} alt="logo" style={{ width: "143px" }} />
      </div>

      <div className="sidebar-divider"></div>
      <div
        style={{
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Menu
          mode="inline"
          theme="light"
          selectedKeys={[selectedId]}
          items={items}
        />
        <div className={`logout-div ${collapsed ? "collapsed" : ""}`}>
          <div className="logout-user" onClick={handleProfileClick}>
            <InitialsAvatar
              className="profile-pic"
              src={profileInfo.avatar}
              name={profileInfo.name}
              alt={profileInfo.name || "Profile"}
              style={{ fontSize: 14 }}
            />

            <div className="logout-user-details">
              <div className="name">{profileInfo.name || "-"}</div>
              <div className="role">{profileInfo.role || "-"}</div>
            </div>

            <div className="logout-icon">
              <img src={logout} alt="logout Icon" />
            </div>
          </div>
        </div>
      </div>
    </Sider>
  );
};

export default SidebarManager;
